import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import useDeviceSize, { SM_BREAKPOINT } from '../hooks/useDeviceSize';
import EbcSolarApi from '../lib/api';
import useEbcSolarStore from '../store/ebc-solar-store';
import { WidgetState } from '../types/states';
import ArrowRight from '../svgs/ArrowRight';

const PostcodeInput = () => {
    const { width, height, isMobile } = useDeviceSize();

    const inputRef = useRef<HTMLInputElement>(null);

    const [isFocused, setFocus] = useState<boolean>(false);
    const [isLoading, setLoading] = useState<boolean>(false);

    const { postcode, setPostcode, setState, setInitialLocation } = useEbcSolarStore((state) => ({
        postcode: state.postcode,
        setPostcode: state.setPostcode,
        setState: state.setState,
        setInitialLocation: state.setInitialLocation,
    }));

    const [lastPostcode, setLastPostcode] = useState<string | null>(null);

    const [suggestions, setSuggestions] = useState<string[]>([]);

    const timer = useRef<any>(null);

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }

        if (!postcode) {
            setSuggestions([]);
            setLastPostcode(null);
        } else if (lastPostcode === null) {
            setSuggestions([postcode]);
        }

        if (!postcode || postcode.length < 4) {
            return;
        }

        const delay = isMobile ? 1500 : 1000; // 1.5s fetch delay on mobile, 1s on desktop

        timer.current = setTimeout(() => {
            fetchSuggestions(postcode);
        }, delay);

        return () => {
            clearTimeout(timer.current);
        };
    }, [postcode]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.onfocus = () => {
                setFocus(true);
                // If mobile keyboard is open, scroll by the height of the keyboard
                if (isMobile && inputRef.current) {
                    const currentScrolled = window.scrollY;

                    // Set minimum scrolled amount to 200px above the input field
                    const minimumScrolled = inputRef.current.height + 200;

                    const toScrollBy = Math.max(0, minimumScrolled - currentScrolled);

                    setTimeout(() => {
                        window.scrollBy({ top: toScrollBy, behavior: 'smooth'});
                    }, 250)
                }
            };
            inputRef.current.onblur = () => {
                setFocus(false);
            };
        }

        return () => {
            if (inputRef.current) {
                inputRef.current.onfocus = null;
                inputRef.current.onblur = null;
            }
        };
    }, [inputRef.current]);

    const fetchSuggestions = async (inputPostcode: string) => {
        if (inputPostcode.length < 4 || inputPostcode.length > 16 || inputPostcode === lastPostcode || isLoading) {
            // console.log('not fetching suggestions for postcode', inputPostcode);
            return;
        }
        // console.log('fetching suggestions for postcode', inputPostcode);

        setLoading(true);
        const result = await EbcSolarApi.queryPostcodeAutocomplete(inputPostcode);
        setLoading(false);

        if (result) {
            const suggestions = result.map((suggestion) => suggestion.description);
            setSuggestions(suggestions);
            setLastPostcode(inputPostcode);
        } else {
            // TODO Handle error
        }
    };

    const handleSubmit: MouseEventHandler<HTMLDivElement> = async (e) => {
        e.preventDefault();
        if (isLoading || !postcode || postcode.length < 4) {
            return;
        }

        setLoading(true);

        const geocodeResult = await EbcSolarApi.queryGeocodeData(postcode);

        if (geocodeResult) {
            const result = geocodeResult[0];
            const { lat, lng } = result.geometry.location;

            setInitialLocation(lat, lng);
            setTimeout(() => {
                setLoading(false);
                setState(WidgetState.SEARCHING_ADDRESS);
            }, 1000);
        } else {
            // TODO Handle error
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                position: 'absolute',
                left: '0',
                top: '0',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: isMobile ? '90vw' : width < SM_BREAKPOINT ? '80vw' : '50vw',
                        cursor: isLoading ? 'default' : 'text',
                        padding: '1rem',
                        borderRadius: '12px',
                        borderBottomLeftRadius: suggestions.length > 0 ? 0 : '12px',
                        borderBottomRightRadius: suggestions.length > 0 ? 0 : '12px',
                        border: isFocused ? '1px solid #26A757' : '1px solid transparent',
                        transition: 'border 0.2s ease-in-out',
                        background: '#FAFAFA',
                        fontSize: '24px',
                        color: '#666666',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                        zIndex: 999,
                    }}
                    onClick={(e) => {
                        if (inputRef.current && !e.defaultPrevented) {
                            inputRef.current.focus();
                        }
                    }}
                >
                    {!isMobile && (
                        <div style={{ display: 'flex' }}>
                            <img
                                width='40px'
                                height='auto'
                                src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenLocation.svg'
                                alt='Location'
                                style={{
                                    marginRight: '1rem',
                                }}
                            />
                        </div>
                    )}
                    <input
                        type='search'
                        value={postcode}
                        onChange={(e) => {
                            if (!isLoading) {
                                setPostcode(e.target.value);
                            }
                        }}
                        ref={inputRef}
                        placeholder='Please enter your post code'
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: isMobile ? '14px' : '24px',
                            fontFamily: 'Montserrat',
                            color: !postcode || postcode === '' ? '#666666' : '#212121',
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            cursor: isLoading ? 'default' : 'pointer',
                        }}
                        onClick={handleSubmit}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '44px',
                                height: '44px',
                                backgroundColor: isLoading ? '#DDDDDD' : '#26A757',
                                borderRadius: '50%',
                            }}
                        >
                            {isLoading ? (
                                <RotatingLines strokeColor='#999999' strokeWidth='4' animationDuration='0.75' width='28' visible={true} />
                            ) : (
                                <ArrowRight width='24' height='24' stroke='#FFFFFF' />
                            )}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        width: isMobile ? '90vw' : width < SM_BREAKPOINT ? '80vw' : '50vw',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginTop: '0.25rem',
                        borderRadius: '12px',
                        borderTopLeftRadius: suggestions.length > 0 ? 0 : '12px',
                        borderTopRightRadius: suggestions.length > 0 ? 0 : '12px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                        zIndex: 999,
                    }}
                >
                    {suggestions.map((suggestion, i) => {
                        return (
                            <div
                                key={`suggestion_${i}`}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '1rem',
                                    background: '#FFFFFF',
                                    fontSize: isMobile ? '14px' : '20px',
                                    color: '#666666',
                                    zIndex: 999,
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    setPostcode(suggestion);
                                    setSuggestions([]);
                                }}
                            >
                                {suggestion}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PostcodeInput;
